/* Il pannello dei filtri, uno solo per due contenitori.
 *
 * Da 1100px sta in barra laterale, sotto sale come foglio dal basso: è lo
 * stesso componente montato due volte, perché due copie con marcatura diversa
 * divergerebbero al primo filtro aggiunto. La BOZZA però è una sola, tenuta
 * dalla schermata: se ognuna delle due copie avesse la sua, girare il telefono
 * a metà modifica cambierebbe i filtri sotto le dita.
 *
 * I filtri si applicano da soli, senza un pulsante di conferma. Ma su 1.507.455
 * righe ogni applicazione è una query, e i due tipi di controllo non si
 * comportano allo stesso modo:
 *
 *  - SCELTA DISCRETA (pastiglie, voci d'elenco, date): un tocco è una decisione
 *    già presa, e la query parte subito;
 *  - CAMPO NUMERICO DIGITATO: "120" si scrive in tre battute, e tre battute non
 *    devono essere tre interrogazioni. Si aspetta una pausa dall'ultima cifra e
 *    si interroga una volta sola. Non è un ripensamento: è il modo di applicare
 *    subito senza tempestare il server.
 *
 * La BOZZA sopravvive per questo: tiene le cifre nell'intervallo fra la battuta
 * e la query. È una sola per le due copie del pannello, tenuta dalla schermata,
 * perché due bozze indipendenti si scambierebbero i filtri sotto le dita al
 * primo cambio di larghezza.
 *
 * Ogni filtro qui corrisponde a un parametro reale di `GET /backtester/search`.
 * Nessun filtro inventato: il server ignorerebbe l'extra in silenzio.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Avvertenza, Campo, Pulsante, type PropsCampo } from '../../componenti';
import { num, periodo, timeframe } from '../../lib/formato';
import type { FaccetteBacktest } from '../../api/tipi';
import type { StatoDati } from '../../api/usaRichiesta';
import { filtriAttivi, type StatoRicerca } from './statoRicerca';
import './backtest.css';

export interface PropsPannelloFiltri {
  bozza: StatoRicerca;
  /** Controlli a scelta discreta: interroga il server SUBITO. */
  onScegli: (patch: Partial<StatoRicerca>) => void;
  /** Campi numerici digitati: interroga dopo la pausa dall'ultima battuta. */
  onDigita: (patch: Partial<StatoRicerca>) => void;
  faccette: FaccetteBacktest | null;
  statoFaccette: StatoDati;
  /** Riga di azioni. In barra laterale sta in fondo al pannello, nel foglio
   *  sta nel piede appiccicato al bordo, dove arriva il pollice. */
  azioni?: ReactNode;
}

/** Numero → stringa per un campo di modulo, senza trasformare `null` in "0". */
function testo(v: number | null): string {
  return v == null ? '' : String(v);
}

function numeroDaCampo(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Pausa dall'ultima battuta prima di interrogare. Sotto i 300ms la seconda
 *  cifra di un numero a due cifre farebbe partire una query di troppo; sopra i
 *  400ms l'attesa si nota e sembra che il campo non abbia preso. */
const PAUSA_DIGITAZIONE_MS = 350;

function vuoto(patch: Partial<StatoRicerca>): boolean {
  return Object.keys(patch).length === 0;
}

/**
 * I filtri applicati, con la bozza ridotta al suo unico compito residuo:
 * tenere le cifre nell'intervallo fra la battuta e la query.
 *
 * `applica` è la funzione che scrive nell'URL — cioè che fa partire la ricerca.
 * Il chiamante la passa perché lo stato vero della ricerca sta nell'indirizzo,
 * non qui dentro.
 */
export function usaFiltriApplicati(
  valore: StatoRicerca,
  applica: (nuovo: StatoRicerca) => void,
) {
  const [bozza, setBozza] = useState<StatoRicerca>(valore);

  /* Sia `valore` sia `applica` cambiano identità a ogni render: nel timer si
     leggono dai ref, altrimenti il rinvio riapplicherebbe uno stato vecchio. */
  const valoreRef = useRef(valore);
  valoreRef.current = valore;
  const applicaRef = useRef(applica);
  applicaRef.current = applica;

  /* Le battute non ancora interrogate. Sono una PATCH e non una bozza intera:
     così un filtro toccato da fuori nel frattempo — una pastiglia rimossa, il
     tasto indietro — non viene resuscitato dal rinvio in attesa. */
  const inAttesa = useRef<Partial<StatoRicerca>>({});
  const rinvio = useRef<number | null>(null);

  const annullaRinvio = useCallback(() => {
    if (rinvio.current != null) {
      clearTimeout(rinvio.current);
      rinvio.current = null;
    }
  }, []);

  /* Risincronizza quando i filtri cambiano da fuori, ma le cifre in attesa
     restano sopra: senza la fusione, l'aggiornamento dell'URL provocato dalla
     query precedente cancellerebbe quello che si sta ancora digitando. */
  useEffect(() => {
    setBozza(vuoto(inAttesa.current) ? valore : { ...valore, ...inAttesa.current });
  }, [valore]);

  /* Nessun `setState` dopo lo smontaggio, e nessuna query per una schermata
     che non c'è più. */
  useEffect(() => annullaRinvio, [annullaRinvio]);

  /* Qualunque cambio di filtro riporta a pagina 1: restare a pagina 40 su un
     insieme più piccolo darebbe una lista vuota che sembra un errore. */
  const componi = (patch: Partial<StatoRicerca>): StatoRicerca => ({
    ...valoreRef.current,
    ...inAttesa.current,
    ...patch,
    page: 1,
  });

  /** Scelta discreta: il tocco è già la decisione, si interroga subito.
   *  Porta con sé le cifre eventualmente in attesa, che sono state digitate
   *  prima e vanno rispettate. */
  const applicaSubito = useCallback(
    (patch: Partial<StatoRicerca>) => {
      annullaRinvio();
      const nuovo = componi(patch);
      inAttesa.current = {};
      setBozza(nuovo);
      applicaRef.current(nuovo);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [annullaRinvio],
  );

  /** Campo digitato: si vede subito nel campo, si interroga dopo la pausa.
   *  Due campi riempiti di seguito finiscono in UNA query, non in due. */
  const applicaDopoLaPausa = useCallback(
    (patch: Partial<StatoRicerca>) => {
      annullaRinvio();
      inAttesa.current = { ...inAttesa.current, ...patch };
      setBozza(componi({}));
      rinvio.current = window.setTimeout(() => {
        rinvio.current = null;
        const nuovo = componi({});
        inAttesa.current = {};
        applicaRef.current(nuovo);
      }, PAUSA_DIGITAZIONE_MS);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [annullaRinvio],
  );

  /** Sostituzione in blocco (azzeramento): butta anche le battute in attesa. */
  const imposta = useCallback(
    (nuovo: StatoRicerca) => {
      annullaRinvio();
      inAttesa.current = {};
      setBozza(nuovo);
    },
    [annullaRinvio],
  );

  return { bozza, applicaSubito, applicaDopoLaPausa, imposta };
}

/** Le azioni del pannello. `onChiudi` c'è solo nel foglio: lì il pannello
 *  copre lo schermo e senza un pulsante di chiusura nel piede — dove arriva il
 *  pollice — l'unica via sarebbe la ✕ in cima, fuori portata dopo dieci filtri. */
export function AzioniFiltri({
  bozza,
  onAzzera,
  onChiudi,
}: {
  bozza: StatoRicerca;
  onAzzera: () => void;
  onChiudi?: () => void;
}) {
  const attivi = filtriAttivi(bozza).length;
  return (
    <div className="sb-colonna" style={{ gap: 'var(--sb-s-4)', flex: 1 }}>
      {onChiudi && (
        <Pulsante variante="primario" largo onClick={onChiudi}>
          Vedi i risultati
        </Pulsante>
      )}
      <Pulsante variante="nudo" largo disabled={attivi === 0} onClick={onAzzera}>
        {attivi === 0 ? 'Nessun filtro attivo' : `Azzera i ${attivi} filtri`}
      </Pulsante>
    </div>
  );
}

export function PannelloFiltri({
  bozza,
  onScegli: scegli,
  onDigita: digita,
  faccette,
  statoFaccette,
  azioni,
}: PropsPannelloFiltri) {
  const [cercaStrategia, setCercaStrategia] = useState('');
  const [cercaGruppo, setCercaGruppo] = useState('');

  const strategie = faccette?.strategie ?? [];
  const gruppi = faccette?.gruppi ?? [];

  const strategieViste = strategie.filter((s) =>
    s.v.toLowerCase().includes(cercaStrategia.trim().toLowerCase()),
  );
  const gruppiVisti = gruppi.filter((g) => {
    const q = cercaGruppo.trim().toLowerCase();
    if (!q) return true;
    return (
      String(g.id).includes(q) ||
      (g.strategyName ?? '').toLowerCase().includes(q) ||
      (g.asset ?? '').toLowerCase().includes(q)
    );
  });

  const faccetteAssenti = statoFaccette === 'errore' || (statoFaccette !== 'caricamento' && !faccette);

  return (
    <div className="sb-bt-filtri">
      <div className="sb-occhiello">Filtri</div>

      {faccetteAssenti && (
        <Avvertenza livello="cautela">
          I valori disponibili non sono arrivati: strategia, asset, timeframe e gruppo restano da
          scrivere a mano ed è facile digitarne uno che non esiste.
        </Avvertenza>
      )}

      {/* ---------- strategia ---------- */}
      <Gruppo
        nome="Strategia"
        conteggio={
          statoFaccette === 'caricamento' ? '…' : `${num(strategie.length)} valori`
        }
      >
        {strategie.length > 12 && (
          <input
            className="sb-campo__input"
            placeholder="cerca fra le strategie"
            value={cercaStrategia}
            onChange={(e) => setCercaStrategia(e.target.value)}
            style={{
              width: '100%',
              minHeight: 'var(--sb-tocco-min)',
              background: 'var(--sb-superficie-2)',
              border: '1px solid var(--sb-bordo)',
              borderRadius: 'var(--sb-r-3)',
              padding: '0 var(--sb-s-4)',
              marginBottom: 'var(--sb-s-3)',
            }}
          />
        )}
        {strategie.length > 0 ? (
          <div className="sb-bt-valori">
            {strategieViste.map((s) => (
              <VoceValore
                key={s.v}
                etichetta={s.v}
                /* `n` arriva come stringa: viene da un COUNT(*) grezzo. */
                conteggio={num(Number(s.n))}
                scelto={bozza.strategyName === s.v}
                onScegli={() =>
                  scegli({ strategyName: bozza.strategyName === s.v ? null : s.v })
                }
              />
            ))}
            {strategieViste.length === 0 && (
              <div className="sb-nota" style={{ padding: 'var(--sb-s-4)' }}>
                Nessuna strategia contiene «{cercaStrategia}».
              </div>
            )}
          </div>
        ) : (
          <Campo
            etichetta="Nome della strategia"
            testuale
            value={bozza.strategyName ?? ''}
            onChange={(e) => digita({ strategyName: e.target.value || null })}
          />
        )}
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
          Una sola strategia per volta: il server confronta con l'uguale, non con un elenco.
        </div>
      </Gruppo>

      {/* ---------- asset ---------- */}
      <Gruppo
        nome="Asset"
        conteggio={
          statoFaccette === 'caricamento' ? '…' : `${num(faccette?.assets.length ?? 0)} valori`
        }
      >
        <div className="sb-bt-pillole">
          {(faccette?.assets ?? []).map((a) => (
            <button
              key={a.v}
              type="button"
              className={`sb-bt-pillola${bozza.asset === a.v ? ' sb-bt-pillola--scelta' : ''}`}
              aria-pressed={bozza.asset === a.v}
              onClick={() => scegli({ asset: bozza.asset === a.v ? null : a.v })}
            >
              {a.v}
              <span className="sb-bt-pillola__n">{num(Number(a.n))}</span>
            </button>
          ))}
        </div>
      </Gruppo>

      {/* ---------- timeframe ---------- */}
      <Gruppo
        nome="Timeframe"
        conteggio={
          statoFaccette === 'caricamento' ? '…' : `${num(faccette?.timeframe.length ?? 0)} valori`
        }
      >
        <div className="sb-bt-pillole">
          {(faccette?.timeframe ?? []).map((t) => (
            <button
              key={t.v}
              type="button"
              className={`sb-bt-pillola${bozza.candleSize === t.v ? ' sb-bt-pillola--scelta' : ''}`}
              aria-pressed={bozza.candleSize === t.v}
              onClick={() => scegli({ candleSize: bozza.candleSize === t.v ? null : t.v })}
            >
              {timeframe(t.v)}
              <span className="sb-bt-pillola__n">{num(Number(t.n))}</span>
            </button>
          ))}
        </div>
      </Gruppo>

      {/* ---------- gruppo ---------- */}
      <Gruppo
        nome="Gruppo"
        conteggio={statoFaccette === 'caricamento' ? '…' : `${num(gruppi.length)} in elenco`}
      >
        {gruppi.length > 12 && (
          <input
            className="sb-campo__input"
            placeholder="cerca per id, strategia o asset"
            value={cercaGruppo}
            onChange={(e) => setCercaGruppo(e.target.value)}
            style={{
              width: '100%',
              minHeight: 'var(--sb-tocco-min)',
              background: 'var(--sb-superficie-2)',
              border: '1px solid var(--sb-bordo)',
              borderRadius: 'var(--sb-r-3)',
              padding: '0 var(--sb-s-4)',
              marginBottom: 'var(--sb-s-3)',
            }}
          />
        )}
        <div className="sb-bt-valori">
          <VoceValore
            etichetta="Tutti i gruppi"
            conteggio=""
            scelto={bozza.backtestGroupId == null}
            onScegli={() => scegli({ backtestGroupId: null })}
          />
          {gruppiVisti.map((g) => (
            <VoceValore
              key={g.id}
              etichetta={`#${g.id} · ${g.strategyName ?? '—'} · ${g.asset ?? '—'}`}
              conteggio={g.backtestNumber == null ? '' : num(g.backtestNumber)}
              scelto={bozza.backtestGroupId === g.id}
              onScegli={() =>
                scegli({ backtestGroupId: bozza.backtestGroupId === g.id ? null : g.id })
              }
            />
          ))}
        </div>
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
          L'elenco si ferma ai 300 gruppi più recenti: è il limite del server.
        </div>
      </Gruppo>

      {/* ---------- copertura ---------- */}
      <Gruppo nome="Il backtest deve coprire" conteggio="intervallo">
        <div style={{ display: 'flex', gap: 'var(--sb-s-4)', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 130px' }}>
            <Campo
              etichetta="Dal"
              type="date"
              value={bozza.startDate ?? ''}
              onChange={(e) => scegli({ startDate: e.target.value || null })}
            />
          </div>
          <div style={{ flex: '1 1 130px' }}>
            <Campo
              etichetta="Al"
              type="date"
              value={bozza.endDate ?? ''}
              onChange={(e) => scegli({ endDate: e.target.value || null })}
            />
          </div>
        </div>
        {/* La semantica è controintuitiva e va scritta, non intuita: un
            backtest 2017–2026 SODDISFA la richiesta "dal 2022 al 2024". */}
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
          Restano i backtest che contengono l'intervallo per intero, non quelli che ci stanno
          dentro: chiedendo 2022–2024 compare anche chi ha misurato 2017–2026.
          {faccette?.periodi?.dal && (
            <>
              {/* `periodo()` e non le date ISO tagliate a mano: «2017-08-17» sta due centimetri
                  sotto due campi che scrivono «17/08/2017», e il mese a parole toglie
                  l'ambiguita' fra 03/04 e 04/03 che nessun formato numerico risolve. */}
              {' '}Il magazzino copre {periodo(faccette.periodi.dal, faccette.periodi.al)}.
            </>
          )}
        </div>
      </Gruppo>

      {/* ---------- soglie numeriche ---------- */}
      <Gruppo nome="Soglie" conteggio="5 campi">
        <CampoNumerico
          etichetta="Operazioni minime"
          suggerimento="tradesNumber ≥"
          inputMode="numeric"
          valore={bozza.minTrades}
          onCambia={(v) => digita({ minTrades: v })}
        />
        <CampoNumerico
          etichetta="Profitto minimo"
          suggerimento="strategyChange ≥"
          unita="%"
          inputMode="decimal"
          valore={bozza.minChange}
          onCambia={(v) => digita({ minChange: v })}
        />
        <CampoNumerico
          etichetta="Drawdown massimo"
          suggerimento="in valore assoluto"
          unita="%"
          inputMode="decimal"
          valore={bozza.maxDrawdown}
          onCambia={(v) => digita({ maxDrawdown: v })}
        />
        <CampoNumerico
          etichetta="Intorno minimo"
          suggerimento="quanto regge cambiando il parametro"
          inputMode="decimal"
          valore={bozza.minIntorno}
          onCambia={(v) => digita({ minIntorno: v })}
        />
        {/* Si scrive in percentuale, come si legge nella colonna: a database e' un rapporto,
            ma far digitare 0,8 dove a schermo c'e' scritto 80% sarebbe chiedere un numero
            diverso da quello che si sta guardando. La conversione la fa il server: una
            seconda divisione qui filtrerebbe per lo 0,8% invece che per l'80%. */}
        <CampoNumerico
          etichetta="Tenuta minima"
          suggerimento="80 = rende quanto prima"
          inputMode="decimal"
          valore={bozza.minTenuta}
          onCambia={(v) => digita({ minTenuta: v })}
        />
        {/* La pausa si nota, e senza una riga che la spieghi sembra che il campo non
            abbia preso il valore. */}
        <div className="sb-nota">
          Le soglie partono un attimo dopo l'ultima cifra: su 1.507.455 righe ogni cifra
          sarebbe una interrogazione. Svuotare il campo toglie il filtro.
        </div>
      </Gruppo>

      {azioni}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Gruppo({
  nome,
  conteggio,
  children,
}: {
  nome: string;
  conteggio: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="sb-bt-filtro__testata">
        <span className="sb-bt-filtro__nome">{nome}</span>
        <span className="sb-bt-filtro__conteggio">{conteggio}</span>
      </div>
      {children}
    </div>
  );
}

/**
 * Un campo numerico che ricorda il TESTO digitato, non solo il numero.
 *
 * Con `value={String(numero)}` il punto decimale appena battuto sparisce: chi
 * scrive "0.5" batte "0.", `Number('0.')` vale 0 e il campo si riscrive come
 * "0" cancellando il punto sotto le dita. Il numero si ricava dal testo, mai il
 * contrario — e la risincronizzazione da fuori scatta solo quando il valore che
 * arriva NON è quello che il testo già rappresenta.
 */
function CampoNumerico({
  valore,
  onCambia,
  ...resto
}: Omit<PropsCampo, 'value' | 'onChange' | 'type'> & {
  valore: number | null;
  onCambia: (v: number | null) => void;
}) {
  const [grezzo, setGrezzo] = useState(testo(valore));

  useEffect(() => {
    if (numeroDaCampo(grezzo) !== valore) setGrezzo(testo(valore));
    /* `grezzo` fuori dalle dipendenze di proposito: rientrandoci, l'effetto
       girerebbe a ogni battuta e normalizzerebbe "0." in "0". */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valore]);

  return (
    <Campo
      {...resto}
      type="number"
      value={grezzo}
      onChange={(e) => {
        setGrezzo(e.target.value);
        /* Campo svuotato: il filtro si toglie, non resta all'ultimo valore. */
        onCambia(numeroDaCampo(e.target.value));
      }}
    />
  );
}

function VoceValore({
  etichetta,
  conteggio,
  scelto,
  onScegli,
}: {
  etichetta: string;
  conteggio: string;
  scelto: boolean;
  onScegli: () => void;
}) {
  return (
    <button
      type="button"
      className={`sb-bt-valore${scelto ? ' sb-bt-valore--scelto' : ''}`}
      aria-pressed={scelto}
      onClick={onScegli}
      title={etichetta}
    >
      <span className="sb-bt-valore__etichetta">{etichetta}</span>
      {conteggio && <span className="sb-bt-valore__n">{conteggio}</span>}
    </button>
  );
}
